import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users as UsersIcon, Plus, Search, X, Shield, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

const roleColor = (r) =>
  r === 'super_admin'    ? 'bg-red-50 text-red-700' :
  r === 'branch_manager' ? 'bg-purple-50 text-purple-700' :
  r === 'recruiter'      ? 'bg-blue-50 text-blue-700' :
  r === 'finance'        ? 'bg-green-50 text-green-700' :
  r === 'client'         ? 'bg-amber-50 text-amber-700' :
  'bg-gray-50 text-gray-600';

const roleDescriptions = {
  super_admin    : 'Full access to everything',
  branch_manager : 'Full access within their branch',
  recruiter      : 'Candidates, jobs, applications',
  finance        : 'Payroll, invoices, payments',
  client         : 'Their own requisitions and workers',
  candidate      : 'Own profile, attendance, payslips'
};

const AddUserModal = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name    : '',
    email   : '',
    phone   : '',
    password: '',
    role    : 'recruiter'
  });
  const [showPassword, setShowPassword] = useState(false);

  const mutation = useMutation({
    mutationFn: (data) => API.post('/auth/register', data),
    onSuccess: () => {
      toast.success('User created successfully!');
      queryClient.invalidateQueries(['users']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create user')
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Add New User</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input name="name" value={form.name} onChange={handle} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Priya Sharma" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input name="email" type="email" value={form.email} onChange={handle} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="priya@devinetechnologies.in" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
            <input name="phone" value={form.phone} onChange={handle} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="9xxxxxxxxx" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
            <select name="role" value={form.role} onChange={handle}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="branch_manager">Branch Manager</option>
              <option value="recruiter">Recruiter</option>
              <option value="finance">Finance</option>
              <option value="client">Client</option>
              <option value="candidate">Candidate</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              {roleDescriptions[form.role]}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <div className="relative">
              <input
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={handle}
                required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Min 8 chars, uppercase, number, special char"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600">
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Example: Recruiter@123
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ResetPasswordModal = ({ user, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [loading,     setLoading]     = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) return toast.error('Passwords do not match');
    if (newPassword.length < 6)  return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      await API.patch(`/users/${user.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${user.name}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Reset Password</h2>
            <p className="text-xs text-gray-400 mt-0.5">Setting new password for {user.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">New Password *</label>
            <input type="password" required value={newPassword} onChange={e => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Min 6 characters" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Confirm Password *</label>
            <input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Repeat new password" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ToggleUserModal = ({ user, onClose }) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => API.put(`/users/${user.id}`, { is_active: !user.is_active }),
    onSuccess: () => {
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      queryClient.invalidateQueries(['users']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update user')
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          {user.is_active ? 'Deactivate User' : 'Activate User'}
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          {user.is_active
            ? `${user.name} will no longer be able to login.`
            : `${user.name} will be able to login again.`}
        </p>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50
              ${user.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
            {mutation.isPending ? 'Updating...' : user.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Users = () => {
  const [search,     setSearch]     = useState('');
  const [role,       setRole]       = useState('');
  const [showAdd,    setShowAdd]    = useState(false);
  const [toggleUser, setToggleUser] = useState(null);
  const [resetUser,  setResetUser]  = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users', search, role],
    queryFn : () => API.get('/users', { params: { search, role } }).then(r => r.data)
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Users</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage staff accounts and access levels</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={16} />
          Add User
        </button>
      </div>

      {/* Role Cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {Object.entries(roleDescriptions).map(([r, desc]) => (
          <div key={r}
            onClick={() => setRole(role === r ? '' : r)}
            className={`cursor-pointer rounded-xl border p-3 transition
              ${role === r ? 'border-blue-300 bg-blue-50' : 'border-gray-100 bg-white hover:bg-gray-50'}`}>
            <Shield size={16} className={role === r ? 'text-blue-600' : 'text-gray-400'} />
            <p className="text-xs font-medium text-gray-700 mt-1 capitalize">{r.replace('_', ' ')}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading users...</div>
        ) : data?.data?.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">No users found.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">User</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Phone</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Role</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Last Login</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.data?.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 transition">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 text-xs font-semibold">
                          {user.name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-400">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">{user.phone}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${roleColor(user.role)}`}>
                      {user.role?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium
                      ${user.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-400">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString('en-IN') : 'Never'}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setResetUser(user)}
                        className="text-xs px-3 py-1.5 rounded-lg font-medium text-blue-600 border border-blue-200 hover:bg-blue-50 flex items-center gap-1">
                        <KeyRound size={11} /> Reset Pwd
                      </button>
                      {user.role !== 'super_admin' && (
                        <button
                          onClick={() => setToggleUser(user)}
                          className={`text-xs px-3 py-1.5 rounded-lg font-medium
                            ${user.is_active
                              ? 'text-red-600 border border-red-200 hover:bg-red-50'
                              : 'text-green-600 border border-green-200 hover:bg-green-50'}`}>
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd    && <AddUserModal    onClose={() => setShowAdd(false)} />}
      {toggleUser && <ToggleUserModal user={toggleUser} onClose={() => setToggleUser(null)} />}
      {resetUser  && <ResetPasswordModal user={resetUser} onClose={() => setResetUser(null)} />}
    </div>
  );
};

export default Users;